import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useSelector } from 'react-redux';
import { useGetMatnrPartsPriceReportQuery } from '../../../../api';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import { matchModeToOperator } from '../../../../utils/filterUtils';

const INITIAL_FILTER_STATE = {
    marka: null,
    markaMatchMode: FilterMatchMode.CONTAINS,
    partCode: null,
    partCodeMatchMode: FilterMatchMode.CONTAINS,
    partDescTr: null,
    partDescTrMatchMode: FilterMatchMode.CONTAINS,
    partDescEn: null,
    partDescEnMatchMode: FilterMatchMode.CONTAINS,
    uom: null,
    uomMatchMode: FilterMatchMode.CONTAINS,
    price: null,
    priceMatchMode: FilterMatchMode.CONTAINS,
    currency: null,
    currencyMatchMode: FilterMatchMode.CONTAINS
};

const backendColumnMap = {
    marka: 'Marka',
    partCode: 'PartCode',
    partDescTr: 'PartDescTr',
    partDescEn: 'PartDescEn',
    uom: 'Uom',
    price: 'Price',
    currency: 'Currency'
};

const TEXT_FIELDS = ['marka', 'partCode', 'partDescTr', 'partDescEn', 'uom', 'price', 'currency'];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState[field];
        const matchMode = filtersState[`${field}MatchMode`];
        const column = backendColumnMap[field];

        if (!column || value === null || value === undefined || value === '') return;

        filters.push({
            column,
            operator: matchModeToOperator[matchMode] ?? 1,
            value: typeof value === 'string' ? value.trim() : String(value).trim()
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;

    const column = backendColumnMap[field];
    if (!column) return null;

    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const useMatnrPartPriceReport = () => {
    const userParamsFromStore = useSelector(selectUserParams);

    const appBranchId = userParamsFromStore?.dealerId ?? userParamsFromStore?.data?.dealerId ?? 0;
    const isDomestic = userParamsFromStore?.isDomestic ?? userParamsFromStore?.data?.isDomestic ?? false;

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1
    });

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);

    // API'ye gidecek ana query parametreleri
    const queryParams = useMemo(() => {
        const params = {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            appBranchId
        };

        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;

        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;

        return params;
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, appBranchId]);

    const { data: apiData, isFetching } = useGetMatnrPartsPriceReportQuery(queryParams);

    const handleFilterChange = (updatedFilters) => {
        setActiveFilters(updatedFilters);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams({ first: 0, rows: 10, page: 1 });
    };

    const onPage = (event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };

    const onSort = (event) => {
        setSortField(event?.sortField || null);
        setSortOrder(event?.sortOrder || 0);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const exportParams = useMemo(() => {
        const { pageNumber, pageSize, ...rest } = queryParams;
        return rest;
    }, [queryParams]);

    return {
        apiData,
        isLoading: isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        onPage,
        onSort,
        handleFilterChange,
        initFilters,
        exportParams,
        isDealer: false,
        isDomestic
    };
};

export default useMatnrPartPriceReport;